// VirtualZones.jsx - Componente con paleta optimizada
import React, { useState } from 'react';
import { 
  FaBrush, 
  FaHeart, 
  FaPaw, 
  FaComments, 
  FaLeaf, 
  FaTimes, 
  FaPlay,
  FaPalette,
  FaHandsHelping,
  FaSeedling,
  FaEye,
  FaMicrophone,
  FaVolumeDown
} from 'react-icons/fa';

const VirtualZones = () => {
  const [selectedZone, setSelectedZone] = useState(null);

  const zones = [
    {
      id: 'creative',
      title: 'Zona de Creación Artística',
      icon: FaBrush,
      color: 'from-emerald-400 to-teal-400', // Verde-suave a verde-agua
      description: 'Libera tu creatividad con nuestro muro digital interactivo',
      shortDesc: 'Muro para dibujar digitalmente, herramientas de pintura, obras flotantes de niños',
      features: ['Pinceles animados', 'Paleta de colores infinita', 'Galería de obras', 'Texturas mágicas'],
      content: {
        type: 'creative',
        tools: ['brush', 'colors', 'stamps', 'effects']
      }
    },
    {
      id: 'sensory',
      title: 'Zona Sensorial-Emocional',
      icon: FaHeart,
      color: 'from-pink-400 to-rose-400', // Rosa-suave optimizado
      description: 'Explora tus sentidos y emociones en un ambiente inmersivo',
      shortDesc: 'Caja sensorial 3D con sonidos, aromas simulados, texturas clicables',
      features: ['Sonidos de la naturaleza', 'Texturas interactivas', 'Aromas visuales', 'Colores que responden'],
      content: {
        type: 'sensory',
        elements: ['sounds', 'textures', 'colors', 'aromas']
      }
    },
    {
      id: 'animal',
      title: 'Zona de Vínculo Animal',
      icon: FaPaw,
      color: 'from-teal-400 to-emerald-400', // Verde-agua a verde-suave
      description: 'Conecta con nuestros amigos animales virtuales',
      shortDesc: 'Peluches animados, avatar de tortuga, historia interactiva de cuidado animal',
      features: ['Amigos virtuales diversos', 'Historias interactivas', 'Cuidado virtual', 'Lecciones de empatía'],
      content: {
        type: 'animal',
        animals: ['turtle', 'dolphin', 'frog', 'dog', 'rabbit', 'elephant', 'tiger']
      }
    },
    {
      id: 'feelings',
      title: 'Zona del Sentir',
      icon: FaComments,
      color: 'from-amber-400 to-orange-400', // Amarillo-alegre a naranja-cálido
      description: 'Un espacio seguro para expresar y compartir emociones',
      shortDesc: 'Círculo de avatares, botón para compartir emociones en emojis, grabadora de voz',
      features: ['Círculo de emociones', 'Expresión con emojis', 'Grabación de voz', 'Flor de la palabra'],
      content: {
        type: 'feelings',
        tools: ['emotions', 'voice', 'sharing', 'circle']
      }
    },
    {
      id: 'introspection',
      title: 'Rincón de Introspección',
      icon: FaLeaf,
      color: 'from-emerald-400 to-teal-500', // Verde-suave a verde-agua
      description: 'Encuentra la calma y conoce tu mundo interior',
      shortDesc: 'Mandalas, espejo virtual, bitácora digital, respirador guiado',
      features: ['Mandalas interactivos', 'Espejo del alma', 'Diario digital', 'Respiración guiada'],
      content: {
        type: 'introspection',
        activities: ['mandala', 'mirror', 'journal', 'breathing']
      }
    }
  ];

  const openZone = (zone) => {
    setSelectedZone(zone);
  };

  const closeZone = () => {
    setSelectedZone(null);
  };

  const renderZoneContent = (zone) => {
    switch(zone.id) {
      case 'creative':
        return <CreativeZoneContent />;
      case 'sensory':
        return <SensoryZoneContent />;
      case 'animal':
        return <AnimalZoneContent />;
      case 'feelings':
        return <FeelingsZoneContent />;
      case 'introspection':
        return <IntrospectionZoneContent />;
      default:
        return null;
    }
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
            <FaLeaf className="text-emerald-500 text-xl" />
            <span className="font-medium text-gray-700">Espacios Virtuales</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-titulo font-bold text-gray-800 mb-4">
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              Zonas FeelLab
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Espacios digitales diseñados especialmente para que los niños exploren, 
            aprendan y conecten con sus emociones de manera divertida e interactiva.
          </p>
        </div>

        {/* Grid de zonas */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {zones.map((zone, index) => {
            const IconComponent = zone.icon;
            return (
              <div
                key={zone.id}
                className="group cursor-pointer"
                onClick={() => openZone(zone)}
              >
                <div className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 border border-gray-100 h-full">
                  {/* Icono y header */}
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${zone.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="text-white text-xl" />
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      Zona {index + 1}
                    </div>
                  </div>

                  {/* Título */}
                  <h3 className="text-xl font-titulo font-semibold text-gray-800 mb-3 group-hover:text-emerald-700 transition-colors">
                    {zone.title}
                  </h3>

                  {/* Descripción corta */}
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {zone.shortDesc}
                  </p>

                  {/* Features preview */}
                  <div className="space-y-2 mb-4">
                    {zone.features.slice(0, 2).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-xs text-gray-500">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${zone.color}`}></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Botón de explorar */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-emerald-600 group-hover:text-emerald-700">
                      Explorar zona
                    </span>
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                      <FaPlay className="text-gray-600 group-hover:text-emerald-600 text-xs ml-0.5" />
                    </div>
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-40 transition-opacity">
                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${zone.color}`}></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to action */}
        <div className="text-center">
          <p className="text-gray-600 mb-6">
            Cada zona está diseñada para estimular diferentes aspectos del desarrollo emocional
          </p>
          <button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
            Comenzar la experiencia
          </button>
        </div>
      </div>

      {/* Modal de zona */}
      {selectedZone && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl w-full">
            {/* Header del modal */}
            <div className={`bg-gradient-to-r ${selectedZone.color} p-6 text-white relative`}>
              <button
                onClick={closeZone}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <FaTimes className="text-sm" />
              </button>
              
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <selectedZone.icon className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-titulo font-bold">{selectedZone.title}</h3>
                  <p className="text-white/90">{selectedZone.description}</p>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {renderZoneContent(selectedZone)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Componentes de contenido para cada zona
const CreativeZoneContent = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-titulo font-semibold text-lg mb-3 flex items-center space-x-2">
          <FaPalette className="text-emerald-500" />
          <span>Herramientas Creativas</span>
        </h4>
        <div className="space-y-3">
          <div className="bg-emerald-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">🖌️ Pinceles Mágicos</h5>
            <p className="text-sm text-gray-600">Pinceles que cambian de color y textura mientras dibujas</p>
          </div>
          <div className="bg-teal-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">🎨 Paleta Infinita</h5>
            <p className="text-sm text-gray-600">Colores que se mezclan y crean nuevos tonos automáticamente</p>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-6 flex items-center justify-center">
        <div className="text-center">
          <FaBrush className="text-4xl text-emerald-500 mb-3 mx-auto" />
          <p className="text-gray-600">Zona de dibujo interactiva</p>
          <button className="mt-3 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm hover:bg-emerald-600 transition-colors">
            Comenzar a crear
          </button>
        </div>
      </div>
    </div>
  </div>
);

const SensoryZoneContent = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-4">
      <div className="bg-pink-50 p-4 rounded-xl text-center">
        <FaVolumeDown className="text-2xl text-pink-500 mx-auto mb-2" />
        <h5 className="font-medium mb-2">Sonidos Naturales</h5>
        <p className="text-xs text-gray-600">Lluvia, océano, bosque...</p>
      </div>
      <div className="bg-rose-50 p-4 rounded-xl text-center">
        <FaHandsHelping className="text-2xl text-rose-500 mx-auto mb-2" />
        <h5 className="font-medium mb-2">Texturas Táctiles</h5>
        <p className="text-xs text-gray-600">Suave, rugoso, liso...</p>
      </div>
      <div className="bg-orange-50 p-4 rounded-xl text-center">
        <FaEye className="text-2xl text-orange-500 mx-auto mb-2" />
        <h5 className="font-medium mb-2">Colores Vivos</h5>
        <p className="text-xs text-gray-600">Responden a tus emociones</p>
      </div>
    </div>
  </div>
);

const AnimalZoneContent = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h4 className="font-titulo font-semibold text-lg flex items-center space-x-2">
          <FaPaw className="text-teal-500" />
          <span>Amigos Virtuales</span>
        </h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg">
            <span className="text-2xl">🐢</span>
            <div>
              <p className="font-medium">Tortuga – Paciencia y calma</p>
              <p className="text-sm text-gray-600">"La tortuga nos recuerda que avanzar con serenidad también nos lleva lejos."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-teal-50 rounded-lg">
            <span className="text-2xl">🐬</span>
            <div>
              <p className="font-medium">Delfín – Alegría y creatividad</p>
              <p className="text-sm text-gray-600">"El delfín nos enseña a jugar con la vida, creando con imaginación y entusiasmo."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
            <span className="text-2xl">🐸</span>
            <div>
              <p className="font-medium">Rana – Asco</p>
              <p className="text-sm text-gray-600">"La rana nos muestra que hasta las emociones incómodas nos ayudan a protegernos y aprender."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
            <span className="text-2xl">🐕</span>
            <div>
              <p className="font-medium">Perro – Amor</p>
              <p className="text-sm text-gray-600">"El perro nos inspira a amar sin condiciones, con lealtad y ternura infinita."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
            <span className="text-2xl">🐰</span>
            <div>
              <p className="font-medium">Conejo – Miedo</p>
              <p className="text-sm text-gray-600">"El conejo nos enseña que el miedo es un guardián que nos invita a ser cautelosos y valientes."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-2xl">🐘</span>
            <div>
              <p className="font-medium">Elefante – Tristeza</p>
              <p className="text-sm text-gray-600">"El elefante nos recuerda que la tristeza es un puente hacia la empatía y la conexión profunda."</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
            <span className="text-2xl">🐅</span>
            <div>
              <p className="font-medium">Tigre – Enojo</p>
              <p className="text-sm text-gray-600">"El tigre nos enseña a transformar la fuerza del enojo en energía para defender lo justo."</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl p-6 flex items-center justify-center">
        <div className="text-center">
          <span className="text-4xl mb-3 block">🐢</span>
          <p className="text-gray-600 mb-3">Interactúa con nuestros amigos</p>
          <button className="bg-teal-500 text-white px-4 py-2 rounded-full text-sm hover:bg-teal-600 transition-colors">
            Conocer amigos
          </button>
        </div>
      </div>
    </div>
  </div>
);

const FeelingsZoneContent = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-titulo font-semibold text-lg mb-3 flex items-center space-x-2">
          <FaComments className="text-amber-500" />
          <span>Expresión Emocional</span>
        </h4>
        <div className="space-y-3">
          <div className="bg-amber-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">😊 Rueda de Emociones</h5>
            <p className="text-sm text-gray-600">Identifica y expresa cómo te sientes</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">🎤 Mi Voz</h5>
            <p className="text-sm text-gray-600">Graba mensajes sobre tus sentimientos</p>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-6 flex items-center justify-center">
        <div className="text-center">
          <FaMicrophone className="text-4xl text-amber-500 mb-3 mx-auto" />
          <p className="text-gray-600 mb-3">Comparte tus emociones</p>
          <button className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm hover:bg-amber-600 transition-colors">
            Empezar a sentir
          </button>
        </div>
      </div>
    </div>
  </div>
);

const IntrospectionZoneContent = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-titulo font-semibold text-lg mb-3 flex items-center space-x-2">
          <FaLeaf className="text-emerald-500" />
          <span>Calma Interior</span>
        </h4>
        <div className="space-y-3">
          <div className="bg-emerald-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">🧘‍♀️ Respiración Guiada</h5>
            <p className="text-sm text-gray-600">Ejercicios para encontrar la calma</p>
          </div>
          <div className="bg-teal-50 p-4 rounded-xl">
            <h5 className="font-medium mb-2">📓 Diario Digital</h5>
            <p className="text-sm text-gray-600">Escribe tus pensamientos y reflexiones</p>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-6 flex items-center justify-center">
        <div className="text-center">
          <FaSeedling className="text-4xl text-emerald-500 mb-3 mx-auto" />
          <p className="text-gray-600 mb-3">Cultiva tu paz interior</p>
          <button className="bg-emerald-500 text-white px-4 py-2 rounded-full text-sm hover:bg-emerald-600 transition-colors">
            Comenzar meditación
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default VirtualZones;